import React, { useRef } from 'react';
import { motion, useMotionValue, useTransform, useReducedMotion } from 'framer-motion';

interface GlowCardProps {
  /** Small uppercase eyebrow above the title */
  eyebrow?: string;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
}

/**
 * Frosted panel that lights up under the cursor. The spotlight follows the
 * pointer, the border warms toward red on hover and the whole card lifts on
 * its way into view.
 */
export const GlowCard: React.FC<GlowCardProps> = ({ eyebrow, title, icon, children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const spotlight = useTransform(
    [mx, my],
    ([x, y]: number[]) =>
      `radial-gradient(420px circle at ${x}px ${y}px, rgba(239,68,68,0.16), transparent 65%)`
  );

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduced || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mx.set(e.clientX - rect.left);
    my.set(e.clientY - rect.top);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 32, filter: 'blur(12px)' }}
      whileInView={reduced ? { opacity: 1 } : { opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-8% 0px' }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      className="group relative overflow-hidden rounded-3xl border border-white/[0.10] bg-white/[0.04] backdrop-blur-2xl transition-[border-color,box-shadow] duration-500 hover:border-red-500/35 hover:shadow-[0_30px_80px_-30px_rgba(220,38,38,0.55)]"
    >
      {/* Cursor spotlight */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: spotlight }}
      />

      {/* Static depth wash + top hairline */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-red-950/25 via-transparent to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
      />

      <div className="relative px-5 pt-5">
        <div className="flex items-center gap-3">
          {icon && (
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-black/40 text-red-400/80 transition-all duration-500 group-hover:border-red-500/40 group-hover:text-red-400 group-hover:shadow-[0_0_18px_-2px_rgba(239,68,68,0.6)]">
              {icon}
            </span>
          )}
          <div className="flex min-w-0 flex-col">
            {eyebrow && (
              <span className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-red-500/70">
                {eyebrow}
              </span>
            )}
            <h3 className="truncate text-lg font-semibold tracking-tight text-white/90 transition-colors duration-300 group-hover:text-white">
              {title}
            </h3>
          </div>
        </div>
        <div className="mt-4 h-px w-full bg-gradient-to-r from-white/[0.14] via-white/[0.05] to-transparent" />
      </div>

      <div className="relative p-5 pt-4">{children}</div>
    </motion.div>
  );
};

export default GlowCard;
